import React, { useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPhone,
  faXmark,
  faShieldHalved,
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';

type LegalModalType = 'aviso-legal' | 'privacidad' | 'cookies' | null;

export const Footer: React.FC = () => {
  const [activeModal, setActiveModal] = useState<LegalModalType>(null);

  const handleOpenModal = (type: LegalModalType) => (e: React.MouseEvent) => {
    e.preventDefault();
    setActiveModal(type);
  };

  const handleCloseModal = () => {
    setActiveModal(null);
  };

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: '#FAF8F5',
        borderTop: '1px solid rgba(158, 119, 54, 0.18)',
        pt: { xs: 7, md: 9 },
        pb: { xs: 11, md: 5 },
        position: 'relative',
        textAlign: 'left',
      }}
    >
      <Container maxWidth="lg">
        {/* Main Footer Grid */}
        <Grid container spacing={{ xs: 4, md: 4 }} justifyContent="space-between" alignItems="flex-start" sx={{ mb: { xs: 5, md: 6 } }}>
          {/* Col 1: Brand & Summary */}
          <Grid item xs={12} md={4} sx={{ textAlign: 'left' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left', gap: 2.2 }}>
              <a href="/" style={{ display: 'inline-block' }}>
                <img
                  src="/images/logo-v2.svg"
                  alt="Hua Hao Yue Yuan | Centro de Masajes Orientales"
                  style={{ height: '36px', display: 'block' }}
                />
              </a>
              <Typography
                variant="body2"
                sx={{
                  color: '#68594F',
                  lineHeight: 1.7,
                  fontSize: '0.88rem',
                  textAlign: 'left',
                  maxWidth: { xs: '100%', sm: '420px', md: '330px' },
                }}
              >
                Centro exclusivo de masajes y bienestar oriental en Barcelona.
                Tradición milenaria, relajación absoluta y atención personalizada para revitalizar tu cuerpo y mente.
              </Typography>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5, flexWrap: 'wrap', justifyContent: 'flex-start' }}>
                <Button
                  href="https://wa.link/kffk9v"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  size="small"
                  startIcon={<FontAwesomeIcon icon={faWhatsapp} />}
                  sx={{
                    background: 'linear-gradient(135deg, #DFBA73 0%, #C5A059 50%, #9E7736 100%)',
                    color: '#1C1612',
                    borderRadius: '20px',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    px: 2.2,
                    py: 0.8,
                    textTransform: 'none',
                    boxShadow: '0 4px 12px rgba(158, 119, 54, 0.22)',
                    transition: 'all 0.2s',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #E6C787 0%, #B89248 50%, #8E6A26 100%)',
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  WhatsApp
                </Button>

                <Button
                  href="tel:+34605245681"
                  variant="outlined"
                  size="small"
                  startIcon={<FontAwesomeIcon icon={faPhone} />}
                  sx={{
                    color: '#1C1612',
                    backgroundColor: '#FFFFFF',
                    borderColor: 'rgba(158, 119, 54, 0.3)',
                    borderRadius: '20px',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    px: 2,
                    py: 0.8,
                    textTransform: 'none',
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: 'rgba(212, 175, 55, 0.08)',
                      borderColor: '#9E7736',
                      color: '#9E7736',
                    },
                  }}
                >
                  605 245 681
                </Button>
              </Box>
            </Box>
          </Grid>

          {/* Col 2: Quick Navigation */}
          <Grid item xs={6} sm={4} md={2} sx={{ textAlign: 'left' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left' }}>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  color: '#1C1612',
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  mb: 2,
                  textAlign: 'left',
                  letterSpacing: '-0.01em',
                }}
              >
                Navegación
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.3, textAlign: 'left', alignItems: 'flex-start' }}>
                {[
                  { label: 'Inicio', href: '/' },
                  { label: 'Tratamientos', href: '#tratamientos' },
                  { label: 'Instalaciones', href: '#instalaciones' },
                  { label: 'Galería', href: '#galeria' },
                  { label: 'Contacto', href: '#contacto' },
                ].map((item) => (
                  <Link
                    key={item.label}
                    href={item.href}
                    sx={{
                      color: '#68594F',
                      fontSize: '0.86rem',
                      textDecoration: 'none',
                      fontWeight: 500,
                      textAlign: 'left',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        color: '#9E7736',
                        transform: 'translateX(3px)',
                      },
                    }}
                  >
                    {item.label}
                  </Link>
                ))}
              </Box>
            </Box>
          </Grid>

          {/* Col 3: Treatments */}
          <Grid item xs={6} sm={4} md={2.5} sx={{ textAlign: 'left' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left' }}>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  color: '#1C1612',
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  mb: 2,
                  textAlign: 'left',
                  letterSpacing: '-0.01em',
                }}
              >
                Tratamientos
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.3, textAlign: 'left', alignItems: 'flex-start' }}>
                {[
                  'Masaje Relajante',
                  'Masaje a 4 Manos',
                  'Aceites Esenciales',
                  'Tradicional Thai',
                ].map((t) => (
                  <Link
                    key={t}
                    href="#tratamientos"
                    sx={{
                      color: '#68594F',
                      fontSize: '0.86rem',
                      textDecoration: 'none',
                      fontWeight: 500,
                      textAlign: 'left',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        color: '#9E7736',
                        transform: 'translateX(3px)',
                      },
                    }}
                  >
                    {t}
                  </Link>
                ))}
              </Box>
            </Box>
          </Grid>

          {/* Col 4: Contact & Hours */}
          <Grid item xs={12} sm={4} md={3.5} sx={{ textAlign: 'left' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left' }}>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  color: '#1C1612',
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  mb: 2,
                  textAlign: 'left',
                  letterSpacing: '-0.01em',
                }}
              >
                Visítanos
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.8, textAlign: 'left', alignItems: 'flex-start' }}>
                {/* Dirección */}
                <Box sx={{ textAlign: 'left' }}>
                  <Typography sx={{ color: '#1C1612', fontWeight: 600, fontSize: '0.86rem', textAlign: 'left' }}>
                    Carrer València 57, 08015 Barcelona
                  </Typography>
                  <Typography sx={{ color: '#68594F', fontSize: '0.82rem', mt: 0.2, textAlign: 'left' }}>
                    Metro Rocafort (L1) · Entença (L5)
                  </Typography>
                  <Link
                    href="https://www.google.com/maps/dir/?api=1&destination=Carrer+Valencia+57+08015+Barcelona"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: '#9E7736',
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      textDecoration: 'none',
                      display: 'inline-block',
                      mt: 0.4,
                      textAlign: 'left',
                      transition: 'color 0.2s',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    Cómo llegar en Google Maps →
                  </Link>
                </Box>

                {/* Horario */}
                <Box sx={{ textAlign: 'left' }}>
                  <Typography sx={{ color: '#9E7736', fontWeight: 600, fontSize: '0.84rem', textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'left' }}>
                    Horario
                  </Typography>
                  <Typography sx={{ color: '#68594F', fontSize: '0.82rem', mt: 0.2, textAlign: 'left' }}>
                    Lunes a Sábado: <strong>9:30 – 21:30</strong>
                  </Typography>
                  <Typography sx={{ color: '#68594F', fontSize: '0.82rem', mt: 0.1, textAlign: 'left' }}>
                    Domingo: <strong>10:30 – 20:30</strong>
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Divider */}
        <Divider sx={{ borderColor: 'rgba(158, 119, 54, 0.16)', mb: 3.5 }} />

        {/* Bottom Bar: Copyright & 3 Legal Links */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: { xs: 2, md: 0 },
            textAlign: 'left',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: '#7E6E64',
              fontSize: '0.82rem',
            }}
          >
            © {new Date().getFullYear()} <strong>Hua Hao Yue Yuan</strong>. Todos los derechos reservados.
          </Typography>

          {/* 3 Legal Links */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1, sm: 2 },
              flexWrap: 'wrap',
              justifyContent: { xs: 'flex-start', md: 'flex-end' },
            }}
          >
            <Button
              onClick={handleOpenModal('aviso-legal')}
              size="small"
              sx={{
                color: '#68594F',
                fontSize: '0.82rem',
                textTransform: 'none',
                fontWeight: 600,
                px: 1.2,
                py: 0.4,
                borderRadius: '12px',
                '&:hover': {
                  color: '#9E7736',
                  backgroundColor: 'rgba(212, 175, 55, 0.08)',
                },
              }}
            >
              Aviso Legal
            </Button>

            <Button
              onClick={handleOpenModal('privacidad')}
              size="small"
              sx={{
                color: '#68594F',
                fontSize: '0.82rem',
                textTransform: 'none',
                fontWeight: 600,
                px: 1.2,
                py: 0.4,
                borderRadius: '12px',
                '&:hover': {
                  color: '#9E7736',
                  backgroundColor: 'rgba(212, 175, 55, 0.08)',
                },
              }}
            >
              Política de Privacidad
            </Button>

            <Button
              onClick={handleOpenModal('cookies')}
              size="small"
              sx={{
                color: '#68594F',
                fontSize: '0.82rem',
                textTransform: 'none',
                fontWeight: 600,
                px: 1.2,
                py: 0.4,
                borderRadius: '12px',
                '&:hover': {
                  color: '#9E7736',
                  backgroundColor: 'rgba(212, 175, 55, 0.08)',
                },
              }}
            >
              Política de Cookies
            </Button>
          </Box>
        </Box>
      </Container>

      {/* Legal Dialog / Modal */}
      <Dialog
        open={activeModal !== null}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            backgroundColor: '#FFFFFF',
            border: '1px solid rgba(158, 119, 54, 0.2)',
            p: { xs: 1, sm: 2 },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 700,
            fontSize: '1.25rem',
            color: '#1C1612',
            pb: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <FontAwesomeIcon icon={faShieldHalved} color="#9E7736" size="sm" />
            <span>
              {activeModal === 'aviso-legal' && 'Aviso Legal'}
              {activeModal === 'privacidad' && 'Política de Privacidad'}
              {activeModal === 'cookies' && 'Política de Cookies'}
            </span>
          </Box>
          <IconButton onClick={handleCloseModal} size="small" sx={{ color: '#68594F' }}>
            <FontAwesomeIcon icon={faXmark} />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ color: '#5C4A3E', fontSize: '0.9rem', lineHeight: 1.75 }}>
          {activeModal === 'aviso-legal' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1C1612' }}>
                1. Información General y Titularidad
              </Typography>
              <Typography variant="body2">
                En cumplimiento de lo dispuesto en la Ley 34/2002, de 11 de julio, de Servicios de la Sociedad de la Información y de Comercio Electrónico (LSSI-CE), se informa de que este sitio web es titularidad de <strong>Hua Hao Yue Yuan</strong>, con domicilio en <strong>Carrer València 57, 08015 Barcelona, España</strong>. Teléfono de contacto: <strong>605 245 681 / 931 050 063</strong>.
              </Typography>

              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1C1612' }}>
                2. Condiciones de Uso y Propiedad Intelectual
              </Typography>
              <Typography variant="body2">
                El acceso y/o uso de este portal atribuye la condición de USUARIO, que acepta los términos y condiciones de uso. Todos los contenidos del sitio web (textos, fotografías, gráficos, logotipos, marcas comerciales y diseño) son propiedad de Hua Hao Yue Yuan o de terceros autorizados y están protegidos por la legislación sobre propiedad intelectual e industrial.
              </Typography>

              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1C1612' }}>
                3. Exclusión de Responsabilidad
              </Typography>
              <Typography variant="body2">
                Hua Hao Yue Yuan no se hace responsable de los daños o perjuicios que pudieran derivarse de interferencias, omisiones, interrupciones o averías telefónicas en el funcionamiento operativo de este sistema electrónico.
              </Typography>
            </Box>
          )}

          {activeModal === 'privacidad' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1C1612' }}>
                1. Responsable del Tratamiento
              </Typography>
              <Typography variant="body2">
                De conformidad con el Reglamento General de Protección de Datos (RGPD UE 2016/679) y la Ley Orgánica 3/2018 (LOPDGDD), le informamos que los datos personales facilitados a través de formularios de contacto, llamadas telefónicas o WhatsApp serán tratados por <strong>Hua Hao Yue Yuan</strong>.
              </Typography>

              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1C1612' }}>
                2. Finalidad del Tratamiento
              </Typography>
              <Typography variant="body2">
                Gestionar las consultas, reservas de citas y solicitudes de información realizadas por los usuarios, así como prestar los servicios de masajes y bienestar solicitados. Los datos no se cederán a terceros salvo obligación legal.
              </Typography>

              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1C1612' }}>
                3. Derechos del Usuario
              </Typography>
              <Typography variant="body2">
                El usuario puede ejercer en cualquier momento sus derechos de acceso, rectificación, supresión, limitación del tratamiento, portabilidad y oposición dirigiéndose a nuestro centro en Carrer València 57, Barcelona o a través de nuestros canales de contacto.
              </Typography>
            </Box>
          )}

          {activeModal === 'cookies' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1C1612' }}>
                1. ¿Qué son las Cookies?
              </Typography>
              <Typography variant="body2">
                Una cookie es un pequeño archivo que se descarga en su dispositivo al acceder a determinadas páginas web, permitiendo a la web almacenar y recuperar información sobre los hábitos de navegación del usuario.
              </Typography>

              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1C1612' }}>
                2. Cookies utilizadas en esta Web
              </Typography>
              <Typography variant="body2">
                Este sitio web utiliza exclusivamente <strong>cookies técnicas y funcionales esenciales</strong> necesarias para el correcto funcionamiento de la navegación y la visualización del mapa interactivo y los recursos web. No se recopilan datos de perfilado con fines comerciales de terceros.
              </Typography>

              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1C1612' }}>
                3. Configuración y Desactivación
              </Typography>
              <Typography variant="body2">
                Usted puede permitir, bloquear o eliminar las cookies instaladas en su equipo mediante la configuración de las opciones del navegador instalado en su ordenador o dispositivo móvil.
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, justifyContent: 'flex-end' }}>
          <Button
            onClick={handleCloseModal}
            variant="contained"
            size="small"
            sx={{
              background: 'linear-gradient(135deg, #DFBA73 0%, #C5A059 50%, #9E7736 100%)',
              color: '#1C1612',
              fontWeight: 700,
              borderRadius: '20px',
              px: 3,
              textTransform: 'none',
            }}
          >
            Entendido
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
